import { useEffect, useRef } from 'react';
import type { Graph } from '@maxgraph/core';
import { CommentBadgeOverlay } from '@/graph/overlays/CommentBadgeOverlay';
import { NoteBadgeOverlay } from '@/graph/overlays/NoteBadgeOverlay';
import { CheckpointBadgeOverlay } from '@/graph/overlays/CheckpointBadgeOverlay';
import { NotificationBadgeOverlay } from '@/graph/overlays/NotificationBadgeOverlay';
import { ApprovalBadgeOverlay } from '@/graph/overlays/ApprovalBadgeOverlay';
import type { GraphComment } from '@/graph-store';
import type { FlowEdgeSpec, OperationNodeSpec } from '@/graph/domain/types';

interface HighlightData {
  comments: Record<string, GraphComment[]>; // operationId -> comments
  notes: Record<string, string>; // operationId -> note text
  checkpoints: Record<string, any[]>; // operationId -> checkpoint objects
  notifications: Record<string, any[]>; // operationId -> notification objects
  approvals: Record<string, any[]>; // operationId -> approver objects
}

interface HighlightVisibility {
  comments: boolean;
  notes: boolean;
  checkpoints: boolean;
  notifications: boolean;
  approvals: boolean;
}

/**
 * Hook to manage all 5 types of highlight badges on graph edges/operations
 * Handles:
 * - Creating badges based on data sources
 * - Showing/hiding badges based on visibility toggles
 * - Cleanup on unmount
 */
export function useHighlightOverlays(
  graph: Graph | null,
  highlightData: HighlightData,
  visibility: HighlightVisibility,
  flowEdges: Record<string, FlowEdgeSpec>,
  operationNodes: Record<string, OperationNodeSpec>,
) {
  // Track all overlays so we can clean them up
  const overlaysRef = useRef<Map<string, any[]>>(new Map());

  useEffect(() => {
    if (!graph) {
      return;
    }

    // Clean up previous overlays
    overlaysRef.current.forEach((overlays) => {
      overlays.forEach((overlay) => {
        const cell = graph.getDataModel().getCell(overlay.cellId);
        if (cell && overlay) {
          graph.removeCellOverlay(cell, overlay);
        }
      });
    });
    overlaysRef.current.clear();



    let addedBadgesCount = 0;
    let commentBadgesCount = 0;
    let notesBadgesCount = 0;
    let checkpointsBadgesCount = 0;
    let notificationsBadgesCount = 0;
    let approvalsBadgesCount = 0;

    // Overlays are a view concern; avoid wrapping in model batchUpdate because
    // that can trigger LayoutManager hierarchical runs during overlay refresh.
    const cells = Object.values(graph.getDataModel().cells || {});
    const edges = cells.filter((cell: any) => cell?.isEdge?.());

    // Process each actual edge from the graph model
    edges.forEach((edgeCell: any) => {
      if (!edgeCell) return;

      const edgeId = edgeCell.id;
      const cell = edgeCell;

      // Get edge source and target
      const edgeSource = edgeCell.source;
      const edgeTarget = edgeCell.target;
      const sourceId = edgeSource?.id;
      const targetId = edgeTarget?.id;

      // Extract operationNodeId from edge ID (format: op-edge-{operationNodeId})
      const operationNodeIdMatch = edgeId.match(/^op-edge-(.+)$/);
      let operationNodeId = operationNodeIdMatch ? operationNodeIdMatch[1] : null;
      
      // For transfer steps with suffixes like -remainder, -transfer, -dest
      // Strip the suffix to get the base operationNodeId for data lookup
      let baseOperationNodeId = operationNodeId;
      let transferSuffix = null;
      if (operationNodeId && operationNodeId.match(/-(remainder|transfer|dest)$/)) {
        transferSuffix = operationNodeId.match(/-(remainder|transfer|dest)$/)?.[1] || null;
        baseOperationNodeId = operationNodeId.replace(/-(remainder|transfer|dest)$/, '');
      }

      // Skip badges for -remainder and -dest edges, only show on -transfer edge
      if (transferSuffix && transferSuffix !== 'transfer') {
        return;
      }

      const edgeOverlays: any[] = [];

      // Comments badge - check multiple ID variations since comments keyed by edgeId/vertexId
      if (visibility.comments) {
        const edgeIdComments = highlightData.comments[edgeId]?.length || 0;
        const operationNodeComments = highlightData.comments[operationNodeId]?.length || 0;
        const baseOperationNodeComments = highlightData.comments[baseOperationNodeId]?.length || 0;
        const sourceComments = highlightData.comments[sourceId]?.length || 0;
        const targetComments = highlightData.comments[targetId]?.length || 0;
        const totalComments = edgeIdComments + operationNodeComments + baseOperationNodeComments + sourceComments + targetComments;

        if (totalComments > 0) {
          commentBadgesCount++;
          const overlay = new CommentBadgeOverlay(edgeId, totalComments);
          graph.addCellOverlay(cell, overlay);
          edgeOverlays.push(overlay);
        }
      }

      // Notes badge - use baseOperationNodeId (strips transfer suffixes)
      if (visibility.notes) {
        const noteText = highlightData.notes[baseOperationNodeId];
        const totalNotes = noteText ? 1 : 0;

        if (totalNotes > 0) {
          notesBadgesCount++;
          const overlay = new NoteBadgeOverlay(edgeId, totalNotes);
          graph.addCellOverlay(cell, overlay);
          edgeOverlays.push(overlay);
        }
      }

      // Checkpoints badge - use baseOperationNodeId
      if (visibility.checkpoints) {
        const totalCheckpoints = (highlightData.checkpoints[baseOperationNodeId]?.length || 0);

        if (totalCheckpoints > 0) {
          checkpointsBadgesCount++;
          const overlay = new CheckpointBadgeOverlay(edgeId, totalCheckpoints);
          graph.addCellOverlay(cell, overlay);
          edgeOverlays.push(overlay);
        }
      }

      // Notifications badge - use baseOperationNodeId
      if (visibility.notifications) {
        const totalNotifications = (highlightData.notifications[baseOperationNodeId]?.length || 0);

        if (totalNotifications > 0) {
          notificationsBadgesCount++;
          const overlay = new NotificationBadgeOverlay(edgeId, totalNotifications);
          graph.addCellOverlay(cell, overlay);
          edgeOverlays.push(overlay);
        }
      }

      // Approvals badge - use baseOperationNodeId
      if (visibility.approvals) {
        const totalApprovals = (highlightData.approvals[baseOperationNodeId]?.length || 0);

        if (totalApprovals > 0) {
          approvalsBadgesCount++;
          const overlay = new ApprovalBadgeOverlay(edgeId, totalApprovals);
          graph.addCellOverlay(cell, overlay);
          edgeOverlays.push(overlay);
        }
      }

      if (edgeOverlays.length > 0) {
        overlaysRef.current.set(edgeId, edgeOverlays);
        addedBadgesCount += edgeOverlays.length;
      }
    });



    // Cleanup on unmount or when dependencies change
    return () => {
      overlaysRef.current.forEach((overlays) => {
        overlays.forEach((overlay) => {
          const cell = graph.getDataModel().getCell(overlay.cellId);
          if (cell && overlay) {
            graph.removeCellOverlay(cell, overlay);
          }
        });
      });
      overlaysRef.current.clear();
    };
  }, [graph, highlightData, visibility, flowEdges, operationNodes]);
}
